// src/pages/ImportStationen.jsx
//
// Stations-Import aus der Planungstabelle (Google Sheet).
//
// Ablauf wie beim Team-Import, mit menschlichem Gate vor dem Schreiben:
//   1. Tabelle einfuegen (im Sheet markieren, kopieren, hier einfuegen) oder als
//      CSV hochladen → wird lokal geparst, Spalten werden erkannt.
//   2. Zuordnung       → jede Zielspalte der App einer Sheet-Spalte zuordnen
//                        (Heuristik schlaegt vor, Admin korrigiert).
//   3. Vorschau        → POST /stations/import { dryRun: true }, schreibt NICHTS.
//   4. Importieren     → POST /stations/import { dryRun: false }.
//
// Validierung und Normalisierung liegen im Backend (services/stationImport.js).
// Bewusst NICHT importiert werden Spalten ohne Gegenstueck in der App (Kategorie,
// Status, Spielzeiten, Notiz, automatische Spalten) und alles, was die Wertung
// beruehrt – Typ, Einheit und Wertungsrichtung stellt man je Station im
// Bearbeiten-Dialog ein.

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, SkipForward, RefreshCw } from "lucide-react";
import Button from "../components/ui/Button";
import { parseCSV, leseDateiAlsText } from "../utils/parseTabular";
import { ZIELFELDER, PFLICHT, autoMap } from "../utils/stationenImport";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { notifyError, notifySuccess } from "../utils/notify";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPPING_STORAGE_KEY = "kg_import_stationen_mapping_v1";

const headerSignatur = (headers) => headers.map((h) => h.trim()).join("|");

function ladeGemerktesMapping(headers) {
  try {
    const roh = JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) || "{}");
    return roh[headerSignatur(headers)] || null;
  } catch {
    return null;
  }
}

function merkeMapping(headers, mapping) {
  try {
    const roh = JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) || "{}");
    roh[headerSignatur(headers)] = mapping;
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(roh));
  } catch {
    // localStorage kann fehlschlagen (privater Modus) – nicht kritisch.
  }
}

const STATUS_META = {
  created: { text: "angelegt", cls: "text-green-400", Icon: CheckCircle2 },
  create: { text: "wird angelegt", cls: "text-green-400", Icon: CheckCircle2 },
  updated: { text: "aktualisiert", cls: "text-blue-400", Icon: RefreshCw },
  update: { text: "wird aktualisiert", cls: "text-blue-400", Icon: RefreshCw },
  skipped: { text: "übersprungen", cls: "text-yellow-400", Icon: SkipForward },
  error: { text: "Fehler", cls: "text-red-400", Icon: AlertTriangle },
};

export default function ImportStationen() {
  const navigate = useNavigate();
  const [quelle, setQuelle] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [aktualisieren, setAktualisieren] = useState(false);
  const [report, setReport] = useState(null); // { dryRun, summary, rows }
  const [busy, setBusy] = useState(false);
  const [eingefuegt, setEingefuegt] = useState("");

  const fehlendePflicht = useMemo(() => PFLICHT.filter((k) => !mapping[k]), [mapping]);

  const uebernehmen = useCallback((text, name) => {
    const { headers: h, rows: r } = parseCSV(text);
    if (h.length === 0 || r.length === 0) {
      notifyError("Keine Datenzeilen gefunden (erste Zeile = Überschriften).");
      return;
    }
    setQuelle(name);
    setHeaders(h);
    setRows(r);
    setMapping(ladeGemerktesMapping(h) || autoMap(h));
    setReport(null);
    notifySuccess(`${r.length} Zeile(n) eingelesen.`);
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try {
      uebernehmen(await leseDateiAlsText(file), file.name);
    } catch (err) {
      console.error("[ImportStationen] Datei-Fehler:", err);
      notifyError("Datei konnte nicht gelesen werden.");
    }
  }, [uebernehmen]);

  const baueZeilen = useCallback(() => {
    return rows.map((row) => {
      const obj = {};
      for (const f of ZIELFELDER) {
        const spalte = mapping[f.key];
        if (spalte && Object.prototype.hasOwnProperty.call(row, spalte)) obj[f.key] = row[spalte];
      }
      return obj;
    });
  }, [rows, mapping]);

  const sende = useCallback(
    async (dryRun) => {
      if (fehlendePflicht.length > 0) {
        notifyError("Bitte zuerst alle Pflichtspalten zuordnen.");
        return;
      }
      // Nur der echte Import ist kritisch, die Vorschau aendert nichts.
      if (!dryRun && !(await kritischeAenderungBestaetigen("Stationen importieren."))) return;
      setBusy(true);
      try {
        merkeMapping(headers, mapping);
        const data = await fetchWithAuth(`${BACKEND_URL}/stations/import`, navigate, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stations: baueZeilen(), dryRun, update: aktualisieren }),
        });
        setReport(data);
        if (dryRun) {
          const s = data.summary;
          notifySuccess(`Vorschau: ${s.anlegbar} neu, ${s.aktualisierbar} zu aktualisieren, ${s.uebersprungen} übersprungen, ${s.fehler} Fehler.`);
        } else {
          notifySuccess(`${data.created} angelegt, ${data.updated} aktualisiert.`);
        }
      } catch (err) {
        console.error("[ImportStationen] Import-Fehler:", err);
        notifyError("Import fehlgeschlagen: " + (err.message || "Unbekannter Fehler"));
      } finally {
        setBusy(false);
      }
    },
    [navigate, headers, mapping, baueZeilen, fehlendePflicht, aktualisieren]
  );

  const spaltenOptionen = ["", ...headers];
  const importiert = report && !report.dryRun;
  const schreibbar = report && report.dryRun ? report.summary.anlegbar + report.summary.aktualisierbar : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Stationen importieren</h1>
        <p className="text-gray-400 mt-1 text-sm">
          Im Google Sheet die Tabelle samt Überschriftenzeile markieren, kopieren und unten einfügen
          (oder das Sheet als CSV herunterladen). Danach die Spalten zuordnen und vor dem Import in der Vorschau prüfen.
        </p>
      </div>

      {/* Einfuegen / Upload */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-xl p-5 space-y-3">
        <label className="text-sm text-gray-300" htmlFor="station-paste">Tabelle einfügen</label>
        <textarea
          id="station-paste"
          value={eingefuegt}
          onChange={(e) => setEingefuegt(e.target.value)}
          rows={5}
          placeholder="Hier mit Strg+V einfügen …"
          className="w-full bg-secondary-800 border border-secondary-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" disabled={!eingefuegt.trim()} onClick={() => uebernehmen(eingefuegt, "eingefügte Tabelle")}>
            Tabelle einlesen
          </Button>
          <span className="text-gray-500 text-sm">oder</span>
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-primary-400 hover:text-primary-300">
            <UploadCloud size={18} /> CSV-Datei auswählen
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
        </div>
        {quelle && <p className="text-gray-400 text-sm">Eingelesen: {quelle}</p>}
      </div>

      {/* Mapping */}
      {headers.length > 0 && (
        <div className="bg-secondary-900 border border-secondary-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet size={18} className="text-primary-500" />
            <h2 className="text-lg font-semibold text-white">Spalten zuordnen</h2>
            <span className="text-gray-400 text-sm">({rows.length} Zeilen)</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ZIELFELDER.map((f) => {
              const fehlt = f.required && !mapping[f.key];
              return (
                <div key={f.key} className="flex flex-col space-y-1">
                  <label className="text-sm text-gray-300">
                    {f.label} {f.required && <span className="text-red-400">*</span>}
                  </label>
                  <select
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    className={`w-full bg-secondary-800 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      fehlt ? "border-red-500" : "border-secondary-700"
                    }`}
                  >
                    {spaltenOptionen.map((opt) => (
                      <option key={opt || "__none"} value={opt} className="bg-secondary-900">
                        {opt === "" ? "— nicht zuordnen —" : opt}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          {fehlendePflicht.length > 0 && (
            <p className="text-yellow-400 text-sm mt-4 flex items-center gap-2">
              <AlertTriangle size={16} />
              Pflichtspalten fehlen: {fehlendePflicht.map((k) => ZIELFELDER.find((f) => f.key === k).label).join(", ")}
            </p>
          )}

          <label className="flex items-start gap-2 mt-5 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={aktualisieren}
              onChange={(e) => { setAktualisieren(e.target.checked); setReport(null); }}
              className="mt-0.5"
            />
            <span>
              Bestehende Stationen aktualisieren
              <span className="block text-gray-500">
                Sonst werden Stationen mit vorhandener Nummer übersprungen. Beim Aktualisieren ändern sich nur
                ausgefüllte Zellen der zugeordneten Spalten – leere Zellen löschen nichts, Wertung, Typ und Lageplan bleiben.
              </span>
            </span>
          </label>

          <div className="flex flex-wrap gap-3 mt-5">
            <Button variant="secondary" onClick={() => sende(true)} disabled={busy || fehlendePflicht.length > 0}>
              {busy ? "Prüfe…" : "Vorschau erstellen"}
            </Button>
            <Button
              onClick={() => sende(false)}
              disabled={busy || !report || report.dryRun === false || schreibbar === 0}
              title={!report ? "Zuerst Vorschau erstellen" : ""}
            >
              {busy ? "Importiere…" : report && report.dryRun ? `${schreibbar} Station(en) importieren` : "Importieren"}
            </Button>
          </div>
        </div>
      )}

      {/* Bericht */}
      {report && (
        <div className="bg-secondary-900 border border-secondary-800 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-1">{importiert ? "Import abgeschlossen" : "Vorschau"}</h2>
          <p className="text-sm text-gray-400 mb-4">
            {report.summary.anlegbar} {importiert ? "angelegt" : "neu"} · {report.summary.aktualisierbar} {importiert ? "aktualisiert" : "zu aktualisieren"} ·{" "}
            {report.summary.uebersprungen} übersprungen · {report.summary.fehler} Fehler · {report.summary.gesamt} gesamt
          </p>

          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto rounded-lg border border-secondary-800">
            <table className="w-full text-sm">
              <thead className="bg-secondary-800 text-gray-300 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Zeile</th>
                  <th className="text-left px-3 py-2 font-medium">Nr.</th>
                  <th className="text-left px-3 py-2 font-medium">Spiel</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  const Icon = meta.Icon;
                  return (
                    <tr key={r.index} className="border-t border-secondary-800">
                      {/* +2: Kopfzeile (1) + 0-basierter Index → Zeilennummer in der Tabelle */}
                      <td className="px-3 py-2 text-gray-400">{r.index + 2}</td>
                      <td className="px-3 py-2 text-gray-300">{r.station_number ?? "—"}</td>
                      <td className="px-3 py-2 text-white">{r.station_name || <span className="text-gray-500">—</span>}</td>
                      <td className={`px-3 py-2 ${meta.cls}`}>
                        <span className="inline-flex items-center gap-1"><Icon size={14} /> {meta.text}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{r.reason || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {importiert && (
            <div className="mt-4 space-y-3">
              {report.summary.anlegbar > 0 && (
                <p className="text-sm text-gray-400">
                  Neue Stationen sind als normale Station mit „mehr ist besser“ angelegt. Schätzspiele, Zeitspiele
                  (Einheit, Wertungsrichtung, Stoppuhr) und Quiz bitte je Station im Bearbeiten-Dialog einstellen.
                </p>
              )}
              <Button variant="secondary" onClick={() => navigate("/station")}>Zu den Stationen</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
